import {
} from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useReactToPrint } from 'react-to-print'
import PrintRecordedDataChart from '../../../../components/Charts/PrintRecordedDataChart'
import RecordedDataChart from '../../../../components/Charts/RecordedDataChart'
import { useFullScreenContainerContext } from '../../../../components/FullScreenContainer/FullScreenContainer'
import { selectPatientByKey } from '../../../../state/patient/patientReducer'
import { getRecordedDataFile } from '../../../../state/recordedData/api'
import {
  selectPatientRecordedDataByKey,
} from '../../../../state/recordedData/recordedDataReducer'
import { RecordedSensorDataForCsv } from '../../../../types/SensorType'
import { generateFullName } from '../../PatientsViewStyle'
import {
  MewsChartsContainer,
  PatientDataContainer,
  PatientDataContentContainer,
  PrintContainer,
  PrintMewsChartsContainer,
  PrintWrapper,
  RecordedDataTabContainer,
  TimeContainer,
} from './RecordedVitalsTabStyle'

import DurationSelector from './DurationSelector';
import MoreOptionsMenu from './MoreOptionsMenu';
import { getPatientRecordedData } from '../../../../state/recordedData/recordedDataReducer';

type RecordedVitalsTabProp = {
  patientKey: string
}

dayjs.extend(utc)

export default function RecordedVitalsTab({ patientKey }: RecordedVitalsTabProp) {
  const recordedData = useSelector(selectPatientRecordedDataByKey(patientKey))?.patientRecordedData;
  const patientData = useSelector(selectPatientByKey(patientKey))
  const [selectedDuration, setSelectedDuration] = useState({
    key: 'latest',
    startTime: 0,
    endTime: 0,
    isCustom: false,
  });

  const durationSelectorRef = useRef<{ handleMenuClick: (arg: { key: string }) => void } | null>(null);


  function getRecordedFilename() {
    const startRange = dayjs(selectedDuration.startTime).format('DD-MM-YY-HH-mm-ss')
    const endRange = dayjs(selectedDuration.endTime).format('DD-MM-YY-HH-mm-ss')
    const patientFirstName = patientData?.firstName
    const patientLastName = patientData?.lastName

    const fileName = `${patientLastName}_${patientFirstName}_Vitals_${startRange}_${endRange}`

    return fileName;
  }

  const contentToPrint = useRef<HTMLDivElement | null>(null)
  const handlePrint = useReactToPrint({
    documentTitle: getRecordedFilename(),
    removeAfterPrint: true,
    onBeforePrint() { },
    content: () => contentToPrint.current,
  })


  const handleRefresh = () => {
    triggerSelection(selectedDuration.key);
  };

  const triggerSelection = (key:string) => {
    if (durationSelectorRef.current) {
      durationSelectorRef.current.handleMenuClick({ key });
    }
  };

  const handleRefreshPrint = () => {
    handleRefresh();
    setTimeout(() => {
      handlePrint();
    }, 2000);
  };


  const { setFullScreenTitle } = useFullScreenContainerContext()

  const handleDownoad = useCallback(async () => {
    const { data } = await getRecordedDataFile(patientKey, selectedDuration.startTime, selectedDuration.endTime)
    if (!data) return
    const formattedData = data.sensorData;
    const convertedToCSV = convertToCSV(formattedData)
    const blob = new Blob([convertedToCSV])
    const fileName = `${getRecordedFilename()}.csv`
    downloadFileFromBinaryData(blob, fileName)
  }, [selectedDuration, patientData, patientKey])



  useEffect(() => {
    setFullScreenTitle(
      <>
      </>
    )

  }, [setFullScreenTitle])

  return (
    <RecordedDataTabContainer style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '0' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', fontSize: '1.2rem' }}>
        <MoreOptionsMenu
          onDownload={handleDownoad}
          onPrint={handlePrint}
          onRefresh={handleRefresh}
          onRefreshPrint={handleRefreshPrint}
          disableDownloadPrint={!recordedData || recordedData.length === 0}
          disableRefresh={selectedDuration.isCustom}
        />

        &nbsp;&nbsp;
        <h2 style={{ marginTop: '0.5rem' }}>Recorded Vitals</h2> &nbsp;&nbsp;&nbsp;
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <span
          className='timeLabel'
          style={{ fontWeight: 'bold' }}
        >
          Duration:&nbsp;&nbsp;&nbsp;
        </span>
        <DurationSelector
          key="vitalsRecorded"
          dispatchMethod={getPatientRecordedData}
          patientKey={patientKey}
          onChange={(duration) => {
            setSelectedDuration(duration);            
          }}
        />
      </div>
      <hr style={{ width: '100%', backgroundColor: 'grey' }} />
      <TimeContainer style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '2.5rem' }}>
        <div>
          <span className='timeLabel'>Start Time: </span>
          <span style={{ fontSize: '1rem' }}>
            {dayjs(selectedDuration.startTime).format('DD-MMM-YY HH:mm:ss')}
          </span>
        </div>
        <div>
          <span className='timeLabel'>End Time: </span>
          <span style={{ fontSize: '1rem' }}>
            {dayjs(selectedDuration.endTime).format('DD-MMM-YY HH:mm:ss')}
          </span>
        </div>
      </TimeContainer>

      <MewsChartsContainer>
        <RecordedDataChart
          endTime={selectedDuration.endTime}
          startTime={selectedDuration.startTime}
          recordedData={recordedData}
          lineChartDataKey='mews'
          lineChartDataName='mews'
        />
        <RecordedDataChart
          endTime={selectedDuration.endTime}
          startTime={selectedDuration.startTime}
          recordedData={recordedData}
          lineChartDataKey='spo2'
          lineChartDataName='spo2'
        />
        <RecordedDataChart
          endTime={selectedDuration.endTime}
          startTime={selectedDuration.startTime}
          recordedData={recordedData}
          lineChartDataKey='pr'
          lineChartDataName='pr'
        />
        <RecordedDataChart
          endTime={selectedDuration.endTime}
          startTime={selectedDuration.startTime}
          recordedData={recordedData}
          lineChartDataKey='temp'
          lineChartDataName='temp'
        />
        <RecordedDataChart
          endTime={selectedDuration.endTime}
          startTime={selectedDuration.startTime}
          recordedData={recordedData}
          lineChartDataKey='resp'
          lineChartDataName='resp'
        />
        <RecordedDataChart
          endTime={selectedDuration.endTime}
          startTime={selectedDuration.startTime}
          recordedData={recordedData}
          lineChartDataName='bp'
          multiLineChartKey={['sys', 'dia']}
        />
      </MewsChartsContainer>
      <PrintContainer ref={contentToPrint}>
        <PrintWrapper>
          {patientData && (
            <PatientDataContainer>
              <PatientDataContentContainer
                style={{ textTransform: 'capitalize' }}
              >
                <h1>
                  {generateFullName(
                    patientData.title,
                    patientData.firstName,
                    patientData.lastName
                  )}
                </h1>
              </PatientDataContentContainer>
              <PatientDataContentContainer>
                <p>
                  <span>{patientData.age}</span>
                  <span> - </span>
                  <span>{patientData.sex}</span>
                  <span> | </span>
                  <span>{patientData.patientId}</span>
                </p>
              </PatientDataContentContainer>

              <TimeContainer>
                <div style={{ fontSize: '1rem' }}>
                  <span
                    className='timeLabel'
                    style={{ fontWeight: 'bold' }}
                  >
                    Duration:{' '}
                  </span>
                  <span>
                    {dayjs(selectedDuration.startTime).format('DD/MM/YYYY HH:mm:ss')}
                  </span>
                  <span> - </span>
                  <span>
                    {dayjs(selectedDuration.endTime).format('DD/MM/YYYY HH:mm:ss')}
                  </span>
                </div>
              </TimeContainer>
            </PatientDataContainer>
          )}

          <PrintMewsChartsContainer>
            <PrintRecordedDataChart
              endTime={selectedDuration.endTime}
              startTime={selectedDuration.startTime}
              recordedData={recordedData}
              lineChartDataKey='mews'
              lineChartDataName='mews'
            />
            <PrintRecordedDataChart
              endTime={selectedDuration.endTime}
              startTime={selectedDuration.startTime}
              recordedData={recordedData}
              lineChartDataKey='spo2'
              lineChartDataName='spo2'
            />
            <PrintRecordedDataChart
              endTime={selectedDuration.endTime}
              startTime={selectedDuration.startTime}
              recordedData={recordedData}
              lineChartDataKey='pr'
              lineChartDataName='pr'
            />
            <PrintRecordedDataChart
              endTime={selectedDuration.endTime}
              startTime={selectedDuration.startTime}
              recordedData={recordedData}
              lineChartDataKey='temp'
              lineChartDataName='temp'
            />
            <PrintRecordedDataChart
              endTime={selectedDuration.endTime}
              startTime={selectedDuration.startTime}
              recordedData={recordedData}
              lineChartDataKey='resp'
              lineChartDataName='resp'
            />
            <PrintRecordedDataChart
              endTime={selectedDuration.endTime}
              startTime={selectedDuration.startTime}
              recordedData={recordedData}
              lineChartDataName='bp'
              multiLineChartKey={['sys', 'dia']}
            />
          </PrintMewsChartsContainer>
        </PrintWrapper>
      </PrintContainer>
    </RecordedDataTabContainer>
  )
}

function convertToCSV(data: RecordedSensorDataForCsv[]): string {
  if (data.length === 0) return ''

  const headers = Object.keys(data[0]).filter(
    (header) => !['createTimestamp', 'patientKey'].includes(header)
  )
  headers.push('createTimestamp')

  const csvRows = [
    headers.join(','),
    ...data.map((row) =>
      headers
        .map(
          (header) =>
            `"${(row[header as keyof typeof row] || '').toString().replace(/"/g, '\\"')}"`
        )
        .join(',')
    ),
  ]

  return csvRows.join('\n')
}

function downloadFileFromBinaryData(
  blob: Blob | MediaSource,
  fileName: string
) {
  const downloadLink = document.createElement('a')
  downloadLink.href = URL.createObjectURL(blob)
  downloadLink.download = fileName
  document.body.appendChild(downloadLink)
  downloadLink.click()
  document.body.removeChild(downloadLink)
}
